import { AlertCircle, Inbox, Loader2, ShieldAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

export const PageLoadingState = ({
  label = "Memuat data...",
  description,
  className,
}) => {
  return (
    <div
      className={cn(
        "flex min-h-[320px] items-center justify-center rounded-3xl border border-dashed border-border/80 bg-white/70 px-6 py-10 text-center shadow-sm",
        className
      )}
    >
      <div className="flex max-w-sm flex-col items-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
        <div className="space-y-1">
          <p className="font-manrope text-lg font-bold text-slate-900">{label}</p>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
      </div>
    </div>
  );
};

export const FullScreenLoadingState = ({
  title = "Menyiapkan aplikasi",
  description = "Mohon tunggu, sesi Anda sedang diverifikasi.",
}) => {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.12),_transparent_38%),linear-gradient(180deg,_rgba(255,255,255,1)_0%,_rgba(248,250,252,1)_100%)] px-6 py-10">
      <div className="w-full max-w-md rounded-3xl border border-white/80 bg-white/95 p-8 text-center shadow-xl shadow-slate-200/70 backdrop-blur-sm">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Loader2 className="h-7 w-7 animate-spin" />
        </div>
        <div className="mt-5 space-y-2">
          <p className="font-manrope text-2xl font-extrabold tracking-tight text-slate-900">{title}</p>
          <p className="text-sm leading-6 text-muted-foreground">{description}</p>
        </div>
      </div>
    </div>
  );
};

export const SectionLoadingState = ({
  label = "Memuat...",
  className,
}) => {
  return (
    <div
      className={cn(
        "flex min-h-[180px] items-center justify-center rounded-2xl border border-dashed border-border/80 bg-white/70 px-6 py-8 text-center shadow-sm",
        className
      )}
    >
      <div className="flex flex-col items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
        <p className="text-sm font-medium text-slate-800">{label}</p>
      </div>
    </div>
  );
};

export const PageEmptyState = ({
  title = "Belum ada data",
  description,
  className,
  icon: Icon = Inbox,
  compact = false,
  action,
}) => {
  return (
    <div
      className={cn(
        "rounded-2xl border border-dashed border-border/80 bg-white/75 text-center shadow-sm",
        compact ? "px-5 py-8" : "px-6 py-12",
        className
      )}
    >
      <div className="mx-auto flex max-w-md flex-col items-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-500">
          <Icon className="h-6 w-6" />
        </div>
        <div className="space-y-1">
          <p className="font-manrope text-lg font-bold text-slate-900">{title}</p>
          {description ? <p className="text-sm leading-6 text-muted-foreground">{description}</p> : null}
        </div>
        {action ? <div className="pt-1">{action}</div> : null}
      </div>
    </div>
  );
};

export const AccessNotice = ({ role, requestedPath, requiredRoles = [] }) => {
  return (
    <Alert className="border-amber-300/80 bg-amber-50/90 text-amber-950 shadow-sm">
      <ShieldAlert className="h-4 w-4 text-amber-700" />
      <AlertTitle className="font-manrope text-sm font-bold text-amber-900">
        Akses dibatasi untuk role {role || "pengguna"}
      </AlertTitle>
      <AlertDescription className="text-amber-900/90">
        {requestedPath ? `Halaman ${requestedPath} ` : "Halaman ini "}
        hanya tersedia untuk role {requiredRoles.join(", ") || "tertentu"}. Anda dialihkan ke halaman yang tersedia untuk akun ini.
      </AlertDescription>
    </Alert>
  );
};

export const InlineHint = ({ className, children }) => {
  return (
    <div className={cn("flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-muted-foreground", className)}>
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
      <div>{children}</div>
    </div>
  );
};
